// =============================
// File: PermissionCreateDrawer.jsx
// =============================
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Select from "react-select";
import { createPermission, updatePermission } from "../../../api/settings/permissions/permissionList";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";
import customSelectStyles from "../../../components/ui/customSelectStyles";

const CreateDrawer = ({ isOpen, onClose, onSaved, data }) => {
  const [form, setForm] = useState({ code: "", name: "", module_id: "", description: "" });
  const isEditing = !!data;
  const [loading, setLoading] = useState(false);

  // Ẩn bottom nav khi mở drawer
  useEffect(() => {
    const nav = document.getElementById("bottom-nav");
    if (!nav) return;
  
    if (isOpen) {
      nav.style.display = "none";
    } else {
      nav.style.display = "";
    }
  
    return () => {
      nav.style.display = "";
    };
  }, [isOpen]);

  const moduleOptions = [
    { value: "crm", label: "CRM" },
    { value: "hrm", label: "HRM" },
    { value: "elearning", label: "E-Learning" },
    { value: "finance", label: "Finance" },
    { value: "reports", label: "Reports" },
    { value: "settings", label: "Settings" },
  ];

  useEffect(() => {
    if (data) setForm({
      code: data.code || "",
      name: data.name || "",
      module_id: data.module_id || "",
      description: data.description || "",
    });
    else setForm({ code: "", name: "", module_id: "", description: "" });
  }, [data]);

  const handleSave = async () => {
    try {
      setLoading(true);
      if (isEditing) await updatePermission(data.code, form);
      else await createPermission(form);
      showSuccessToast("Lưu thành công");
      onSaved();
    } catch (err) {
      showErrorToast("Lỗi khi lưu quyền");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-lg border-l z-50"
        >
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <h2 className="text-lg font-semibold">{isEditing ? "Update permission" : "New permission"}</h2>
            <button onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <form className="px-6 py-4 space-y-4 overflow-y-auto h-[calc(100%-140px)]" onSubmit={(e) => e.preventDefault()}>
            <div>
              <label className="text-sm font-medium block mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: crm_all"
                className="w-full border p-2 rounded"
                value={form.code}
                disabled={isEditing}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: Quản lý CRM"
                className="w-full border p-2 rounded"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">
                Modules / Functions <span className="text-red-500">*</span>
              </label>
              <Select
                styles={customSelectStyles}
                options={moduleOptions}
                placeholder="Select module or function"
                value={moduleOptions.find((m) => m.value === form.module_id) || null}
                onChange={(opt) => setForm({ ...form, module_id: opt?.value || "" })}
                isDisabled={loading}
                isClearable
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Description</label>
              <textarea
                rows={4}
                placeholder="Ex: Quản lý học phí và chi phí"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </form>

          <div className="flex justify-end gap-3 px-4 py-4 border-t">
            <button
              onClick={onClose}
              className="border border-gray-300 px-4 py-2 rounded hover:bg-gray-50"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-60"
              disabled={loading || !form.code || !form.name || !form.module_id}
              type="button"
            >
              {isEditing ? "Save changes" : "Save"}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateDrawer;